/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import { compose, withProps } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import Retracked from 'js/app/retracked';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import store from 'js/lib/coursera.store';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import user from 'js/lib/user';

import { useEnterpriseRoles } from 'bundles/enterprise-admin-navigation/utils/withManagedOrganizations';
import GrowthExperiments from 'bundles/epic/clients/Growth';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import type { OrganizationType } from 'bundles/naptimejs/resources/__generated__/ThirdPartyOrganizationsV1';
import NotificationPreferencesV2 from 'bundles/naptimejs/resources/notificationPreferences.v2';
import type { NotificationPreferencesV2Type } from 'bundles/naptimejs/resources/notificationPreferences.v2';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import GDPRShapesSVG from 'bundles/user-consent/components/GDPRShapesSVG';
import { STORAGE_KEY } from 'bundles/user-consent/constants/GdprConstants';
import { getGradient, gradient, showBanner } from 'bundles/user-consent/utils/gdprUtils';

import _t from 'i18n!nls/user-consent';

const styles = {
  GDPRBanner: css`
    padding: var(--cds-spacing-150);
    text-align: center;
    background-image: linear-gradient(90deg, #48cfad, '#3bafda');
    position: relative;
  `,
  textWrapper: css`
    display: inline-block;
    vertical-align: middle;
    font-weight: 600;
    margin: 0;
  `,
  selectWrapper: css`
    width: 100;
    display: inline-block;
    vertical-align: middle;
    margin: var(--cds-spacing-50) var(--cds-spacing-200);
  `,
  selectComponent: css`
    background-color: #fff;
    border: 1px solid #ccc;
    color: #666;
    cursor: default;
    border-radius: 0;
    border-spacing: 0;
    border-collapse: separate;
    height: 36px;
    line-height: 36px;
    outline: none;
    position: relative;
    padding: 0 7px;
  `,
  disabledOption: css`
    display: none;
  `,
};

type InputProps = {
  addParentHeightClass?: (className: string) => void;
  removeParentHeightClass?: (className: string) => void;
  programs?: Array<$TSFixMe>;
  organizationType?: OrganizationType;
};

type PropsFromHooks = {
  isEnterpriseAdmin: boolean;
};

type Props = InputProps &
  PropsFromHooks & {
    notificationPreferences?: NotificationPreferencesV2Type;
    naptime: InjectedNaptime;
    isAdmin?: boolean;
    isEnterprise?: boolean;
    shouldRender: boolean;
  };

type State = {
  isDismissed: boolean;
  selectedValue?: string;
  bannerText: string;
  untranslatedBannerText: string;
  color: string;
};

const generateBannerText = (isEnterprise?: boolean, organizationType?: OrganizationType) => {
  const CONSUMER_BANNER_TEXTS = [
    _t(
      'Please stay with us! Opt-in to let us know you’d like to keep receiving course recommendations via email from Coursera.'
    ),
    _t(
      'Don’t lose touch! Opt-in today to continue receiving emails with course recommendations and news from Coursera.'
    ),
    _t('Stay on track — the deadline is coming soon! Opt-in to continue receiving updates from Coursera.'),
    _t('Don’t miss out. You must opt-in to continue receiving updates from Coursera.'),
  ];
  const CONSUMER_UNTRANSLATED_TEXTS = [
    'Please stay with us! Opt-in to let us know you’d like to keep receiving course recommendations via email from Coursera.',
    'Don’t lose touch! Opt-in today to continue receiving emails with course recommendations and news from Coursera.',
    'Stay on track — the deadline is coming soon! Opt-in to continue receiving updates from Coursera.',
    'Don’t miss out. You must opt-in to continue receiving updates from Coursera.',
  ];
  const originalEnterpriseMessage = _t(
    'Don’t lose touch! Opt-in for updates on how Coursera for Business can help you transform your workforce.'
  );
  const ENTERPRISE_BANNER_TEXTS = [
    organizationType
      ? {
          BUSINESS: originalEnterpriseMessage,
          CAMPUS: _t('Don’t lose touch! Opt-in for updates on Coursera for Campus.'),
          GOVERNMENT: _t(
            'Don’t lose touch! Opt-in for updates on how Coursera for Government can help you transform your workforce.'
          ),
        }[organizationType]
      : originalEnterpriseMessage,
  ];
  const ENTERPRISE_UNTRANSLATED_TEXTS = [
    'Don’t lose touch! Opt-in for updates on how Coursera for Business can help you transform your workforce.',
  ];
  const textResource = isEnterprise ? ENTERPRISE_BANNER_TEXTS : CONSUMER_BANNER_TEXTS;
  const untranslatedTextResource = isEnterprise ? ENTERPRISE_UNTRANSLATED_TEXTS : CONSUMER_UNTRANSLATED_TEXTS;
  const index = Math.floor(Math.random() * textResource.length);

  return { translated: textResource[index], untranslated: untranslatedTextResource[index] };
};

class GDPRBanner extends React.Component<Props, State> {
  static contextTypes = {
    _eventData: PropTypes.object,
  };

  declare context: LegacyContextType<typeof GDPRBanner.contextTypes>;

  constructor(props: Props) {
    super(props);

    const { isAdmin, isEnterprise, organizationType } = props;
    const { translated, untranslated } = generateBannerText(isEnterprise, organizationType);
    const color = getGradient(isEnterprise && isAdmin);

    this.state = {
      isDismissed: false,
      selectedValue: undefined,
      color,
      bannerText: translated,
      untranslatedBannerText: untranslated,
    };
  }

  componentDidMount() {
    this.updateParentClass();
  }

  updateParentClass = () => {
    const { addParentHeightClass, shouldRender } = this.props;
    const { selectedValue } = this.state;
    if (shouldRender && selectedValue !== 'no') {
      addParentHeightClass?.('with-gdpr-banner');
    }
  };

  hideBanner = () => {
    const { removeParentHeightClass } = this.props;
    this.setState(() => ({
      isDismissed: true,
    }));
    removeParentHeightClass?.('with-gdpr-banner');
  };

  selectOnChange = (event: $TSFixMe) => {
    const { untranslatedBannerText, color } = this.state;
    const { _eventData } = this.context;
    const selectedValue = event?.target?.value;

    this.setState(() => ({
      selectedValue,
    }));
    Retracked.trackComponent(
      _eventData,
      { selectedValue, color, bannerText: untranslatedBannerText },
      'gdpr_marketing_dropdown',
      'select'
    );
    if (selectedValue === 'no') {
      this.hideBanner();
      store.set(STORAGE_KEY, true);
    } else {
      setTimeout(this.hideBanner, 8000);
      this.updateNotifications();
    }
  };

  updateNotifications = () => {
    const { naptime, notificationPreferences, isEnterprise, isAdmin } = this.props;
    if (!notificationPreferences) return;

    const newPreference = JSON.parse(JSON.stringify(notificationPreferences));

    if (notificationPreferences && !isEnterprise && !isAdmin) {
      newPreference.global = 'ENABLED';
      newPreference.volunteer = newPreference.volunteer ?? {};
      newPreference.volunteer.email = 'SUBSCRIBED';
      newPreference.promotions = newPreference.promotions ?? {};
      newPreference.promotions.email = 'SUBSCRIBED';
      newPreference.recommendations = newPreference.recommendations ?? {};
      newPreference.recommendations.email = 'SUBSCRIBED';
    } else if (notificationPreferences && isEnterprise && isAdmin) {
      newPreference.global = 'ENABLED';
      newPreference.enterpriseMarketing = newPreference.enterpriseMarketing ?? {};
      newPreference.enterpriseMarketing.email = 'SUBSCRIBED';
    }

    naptime.executeMutation(NotificationPreferencesV2.update(user.get().id, newPreference));
  };

  render() {
    const { shouldRender } = this.props;
    const { isDismissed, selectedValue, bannerText, untranslatedBannerText, color } = this.state;

    if (!shouldRender || isDismissed) {
      return <span />;
    }

    return (
      <TrackedDiv
        css={styles.GDPRBanner}
        trackingName="gdpr_marketing_banner"
        withVisibilityTracking
        data={{ bannerText: untranslatedBannerText, color }}
        style={{
          backgroundImage: `linear-gradient(90deg, ${gradient[color].start}, ${gradient[color].end})`,
        }}
      >
        <GDPRShapesSVG
          className="GDPRShapesSVG"
          style={{ maxWidth: 300, maxHeight: '100%', position: 'absolute', left: 0, top: 0 }}
        />
        <label htmlFor="GDPRDropdown" css={styles.textWrapper}>
          {selectedValue === 'yes' ? (
            <FormattedMessage
              message={_t(
                'Great! You’re signed up. You can manage your communication preferences in your {link}. Thanks for your interest in Coursera, and good luck with your courses.'
              )}
              link={<a href="/account-settings">{_t('Account Settings')}</a>}
            />
          ) : (
            bannerText
          )}
        </label>
        {selectedValue !== 'yes' && (
          <div css={styles.selectWrapper}>
            <select css={styles.selectComponent} id="GDPRdropdown" onChange={this.selectOnChange} required={true}>
              <option
                css={styles.disabledOption}
                key="GDPRdropdown~placeholder"
                value=""
                disabled={true}
                hidden={true}
                selected={true}
              >
                {_t('Select...')}
              </option>
              <option key="GDPRdropdown~yes" value="yes">
                {_t('Yes')}
              </option>
              <option key="GDPRdropdown~no" value="no">
                {_t('Not now')}
              </option>
            </select>
          </div>
        )}
      </TrackedDiv>
    );
  }
}

const gdprCheck = () =>
  Boolean(GrowthExperiments.get('enableGDPR') && user.isAuthenticatedUser() && !store.get(STORAGE_KEY));

const GDPRBannerWithHOCs = compose<Props, InputProps & PropsFromHooks>(
  Naptime.createContainer<Pick<Props, 'notificationPreferences'>, {}>(() => {
    const notificationPreferences = gdprCheck() ? NotificationPreferencesV2.get(user.get().id, {}) : undefined;

    return { notificationPreferences };
  }),
  withProps<
    Omit<Props, 'naptime' | 'addParentHeightClass' | 'removeParentHeightClass'>,
    Pick<Props, 'notificationPreferences' | 'programs' | 'isEnterpriseAdmin'>
  >((props) => {
    const { notificationPreferences, programs, isEnterpriseAdmin } = props;
    const isAdmin = isEnterpriseAdmin;
    // Programs is populated unless on the admin dashboard, where it knows the user is an admin
    const isEnterprise = isAdmin || !!(programs || []).length;
    const shouldRender = showBanner(notificationPreferences, isEnterprise, isAdmin);

    return {
      ...props,
      isEnterprise,
      isAdmin,
      shouldRender,
    };
  })
)(GDPRBanner);

const GDPRBannerWithHooks = (props: InputProps) => {
  const { isEnterpriseAdmin, loading } = useEnterpriseRoles(user.get().id, !gdprCheck());
  if (loading) return null; // similar to the waitForGraphQL HOC above
  return <GDPRBannerWithHOCs {...props} isEnterpriseAdmin={isEnterpriseAdmin} />;
};

export default deferToClientSideRender<InputProps>(GDPRBannerWithHooks);

export { GDPRBanner as BaseComponent };
